import type { Theme } from "@glideapps/glide-data-grid";

/* Canvas fillStyle cannot resolve a CSS variable, so the grid gets hex read off :root. */

/** jsdom has no stylesheet or canvas, so unresolved tokens use these light-theme hex values. */
const TOKEN_FALLBACKS: Record<string, string> = {
  "--ink": "#0C0A09",
  "--subtle": "#656260",
  "--surface": "#FFFFFF",
  "--surface-sunken": "#FAF9F8",
  "--surface-raised": "#FFFFFF",
  "--line": "#D0CDCC",
  "--brand-bold": "#00786F",
  "--on-brand": "#F0FDFA",
  "--link": "#00544E",
  "--grid-header": "#F9F9F8",
  "--grid-header-hover": "#F1F0EF",
  "--grid-header-focus": "#E9E8E6",
  "--grid-header-selected": "#E2E1DE",
  "--warning-subtle": "#FFF7C2",
  "--information-subtle": "#E6F4FE",
  "--danger-subtle": "#FEEBEC",
  "--danger": "#CE2C31",

  "--sand-2": "#F9F9F8",
  "--sand-3": "#F1F0EF",
  "--sand-4": "#E9E8E6",
  "--sand-5": "#E2E1DE",
  "--sand-12": "#21201C",
  "--teal-9": "#12A594",
  "--teal-10": "#0D9B8A",
  "--cyan-9": "#00A2C7",
  "--cyan-10": "#0797B9",
  "--blue-9": "#0090FF",
  "--blue-10": "#0588F0",
  "--amber-9": "#FFC53D",
  "--orange-9": "#F76B15",
};

/**
 * Text for a fill the workbook chose. Theme ink flips with the theme, so the
 * dark theme's sand-12 is near-white; these are the light theme's sand-12 and
 * sand-2, which read the same on a given fill in either theme.
 */
export const FIXED_INK = { onLight: "#21201C", onDark: "#F9F9F8" } as const;

/** The white sheet a workbook's colours were chosen on. */
export const WORKBOOK_PAPER = "#FFFFFF";

const toHexChannel = (channel: number): string =>
  channel.toString(16).padStart(2, "0").toUpperCase();

let probe: CanvasRenderingContext2D | null | undefined;

/** Paint one pixel so a theme color becomes hex. Null when there is no canvas. */
export const cssColorToHex = (value: string): string | null => {
  if (!value.trim() || typeof document === "undefined") return null;
  if (probe === undefined) {
    try {
      probe =
        document
          .createElement("canvas")
          .getContext("2d", { willReadFrequently: true }) ?? null;
    } catch {
      probe = null;
    }
  }
  if (!probe) return null;
  probe.fillStyle = "#010203";
  probe.fillStyle = value.trim();
  if (probe.fillStyle === "#010203") return null;
  probe.clearRect(0, 0, 1, 1);
  probe.fillRect(0, 0, 1, 1);
  const { data } = probe.getImageData(0, 0, 1, 1);
  const rgb = `#${toHexChannel(data[0] ?? 0)}${toHexChannel(data[1] ?? 0)}${toHexChannel(data[2] ?? 0)}`;
  return data[3] === 255 ? rgb : `${rgb}${toHexChannel(data[3] ?? 0)}`;
};

const resolvedTokens = new Map<string, string>();
let cachedAppearance: "light" | "dark" | undefined;

const currentAppearance = (): "light" | "dark" =>
  typeof document !== "undefined" &&
  document.documentElement?.classList.contains("dark")
    ? "dark"
    : "light";

/** Memoized: cell draws call this every frame, and `getComputedStyle` forces style resolution. */
export const volcaColor = (token: string): string => {
  const appearance = currentAppearance();
  if (cachedAppearance !== appearance) {
    resolvedTokens.clear();
    cachedAppearance = appearance;
  }
  const cached = resolvedTokens.get(token);
  if (cached !== undefined) return cached;

  const fallback = TOKEN_FALLBACKS[token] ?? "#000000";
  const resolved =
    typeof window === "undefined"
      ? null
      : cssColorToHex(
          window
            .getComputedStyle(document.documentElement)
            .getPropertyValue(token),
        );

  const value = resolved ?? fallback;
  resolvedTokens.set(token, value);
  return value;
};

/** Clears the memo. Exported for tests that swap the stylesheet under us. */
export const clearVolcaColorCache = (): void => resolvedTokens.clear();

/** A token at partial opacity, for the fills Glide expects to be translucent. */
export const volcaColorAlpha = (token: string, alpha: number): string => {
  const hex = volcaColor(token).slice(1);
  const [r, g, b] = [0, 2, 4].map((i) =>
    Number.parseInt(hex.slice(i, i + 2), 16),
  );
  const tokenAlpha =
    hex.length === 8 ? Number.parseInt(hex.slice(6, 8), 16) / 255 : 1;
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha)) * tokenAlpha})`;
};

/** Glide's stock theme is an indigo accent. Sizes are px because a canvas cannot read a Tailwind class. */
export const buildSpreadsheetTheme = (
  appearance = currentAppearance(),
): Partial<Theme> => ({
  accentColor: volcaColor("--brand-bold"),
  accentFg: volcaColor("--on-brand"),
  accentLight: volcaColorAlpha("--ink", appearance === "dark" ? 0.1 : 0.06),

  // Semantic tokens wherever one exists, so the grid follows what the app means
  // by "primary text" or "a border" rather than a ramp step that happens to
  // match it today. The grid's own DOM chrome already does this — the formula
  // bar's dividers are `border-line`.
  textDark: volcaColor("--ink"),
  textMedium: volcaColor("--subtle"),
  textLight: volcaColor("--subtle"),
  textBubble: volcaColor("--ink"),
  textHeader: volcaColor("--subtle"),
  textGroupHeader: volcaColor("--subtle"),
  // Glide assumes a selected header is filled with the accent and so wants
  // inverse ink here. `drawSpreadsheetHeader` repaints that fill grey instead,
  // so the title stays normal ink — see the comment there.
  textHeaderSelected: volcaColor("--ink"),

  bgIconHeader: volcaColor("--subtle"),
  fgIconHeader: volcaColor("--surface"),

  bgCell: volcaColor("--surface"),
  bgCellMedium: volcaColor("--surface-sunken"),

  // selected cell. These three are interaction states with no semantic token of
  // progression for a fully selected column (`drawSpreadsheetHeader`).
  bgHeader: volcaColor("--grid-header"),
  bgHeaderHovered: volcaColor("--grid-header-hover"),
  bgHeaderHasFocus: volcaColor("--grid-header-focus"),

  bgBubble: volcaColor("--surface-sunken"),
  bgBubbleSelected: volcaColor("--surface"),
  bgSearchResult: volcaColor("--warning-subtle"),

  borderColor: volcaColor("--line"),
  linkColor: volcaColor("--link"),

  // Canvas cannot read a Tailwind class. 13px is the density these column widths were authored for.
  fontFamily: '"Geist Variable", sans-serif',
  baseFontStyle: "13px",
  editorFontSize: "13px",
  headerFontStyle: "600 13px",
  markerFontStyle: "12px",
});
